import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Download, Play } from "lucide-react";
import { MaskLine, Reveal, ClipReveal } from "@/components/Reveal";
import { api, resolveImg } from "@/lib/api";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/products");
        setProducts(data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="products-page">
      {/* HEADER */}
      <section className="bg-[#eaf6fc] pt-40 pb-24 relative overflow-hidden border-b border-black/10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 relative z-10">
          <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#3da8d8] mb-8">Our Product Lines</p>
          <h1 className="font-display font-bold text-[#334155] text-5xl sm:text-7xl lg:text-8xl tracking-tight leading-[0.98]">
            <MaskLine delay={0.1}>{products.length ? `${products.length} categories.` : "Product lines."}</MaskLine>
            <MaskLine delay={0.25}>One standard of <span className="text-[#3da8d8]">craft.</span></MaskLine>
          </h1>
        </div>
      </section>

      {/* SECTIONS */}
      {!loading && products.map((p, i) => {
        const reversed = i % 2 === 1;
        return (
          <section
            key={p.id}
            id={p.slug}
            data-testid={`product-section-${p.slug}`}
            className={`border-b border-black/10 ${i % 2 === 1 ? "bg-white" : "bg-[#F9FAFB]"}`}
          >
            <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32">
              <div className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${reversed ? "lg:[&>*:first-child]:order-2" : ""}`}>
                <ClipReveal className="relative overflow-hidden border border-black/10">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={resolveImg(p.image)} alt={p.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1200ms] ease-out" />
                  </div>
                </ClipReveal>

                <div>
                  <Reveal>
                    <h2 className="font-display text-3xl lg:text-5xl tracking-tight">{p.title}</h2>
                    <p className="font-mono-accent text-xs uppercase tracking-widest text-[#3da8d8] mt-4">{p.tagline}</p>
                  </Reveal>
                  <Reveal delay={0.1}>
                    <p className="mt-6 text-lg text-[#52525B] leading-relaxed">{p.description}</p>
                  </Reveal>
                  <Reveal delay={0.2}>
                    <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                      {(p.features || []).map((f) => (
                        <li key={f} className="flex items-center gap-3 text-[#0A0D14]">
                          <span className="w-6 h-6 flex items-center justify-center bg-[#78be27]/15 shrink-0">
                            <Check size={14} className="text-[#78be27]" />
                          </span>
                          <span className="text-sm">{f}</span>
                        </li>
                      ))}
                    </ul>
                  </Reveal>
                  <Reveal delay={0.3}>
                    <div className="mt-10 flex flex-wrap items-center gap-4">
                      <Link
                        to={`/products/${p.slug}`}
                        data-testid={`product-know-more-${p.slug}`}
                        className="btn-fill group inline-flex items-center gap-3 border border-[#3da8d8] px-7 py-3.5 font-mono-accent text-xs uppercase tracking-widest text-[#3da8d8] hover:text-white"
                      >
                        Know More <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                      </Link>
                      {p.pdf && (
                        <a
                          href={resolveImg(p.pdf)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`product-catalogue-${p.slug}`}
                          className="group inline-flex items-center gap-3 border border-black/15 px-7 py-3.5 font-mono-accent text-xs uppercase tracking-widest text-[#334155] hover:border-[#78be27] hover:text-[#78be27] transition-colors"
                        >
                          Catalogue <Download size={14} className="group-hover:translate-y-0.5 transition-transform" />
                        </a>
                      )}
                      {p.show_videos_button && (
                        <Link
                          to={`/videos?tab=${p.category_key || "in-plant"}`}
                          data-testid={`product-videos-${p.slug}`}
                          className="group inline-flex items-center gap-3 border border-[#78be27] px-7 py-3.5 font-mono-accent text-xs uppercase tracking-widest text-[#78be27] hover:bg-[#78be27] hover:text-white transition-colors"
                        >
                          Videos <Play size={14} className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                      )}
                    </div>
                  </Reveal>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {loading && (
        <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24">
          <p className="font-mono-accent text-xs uppercase tracking-widest text-[#52525B]">Loading products…</p>
        </section>
      )}

      {/* CTA */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 text-center lg:text-left">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
          <h2 className="font-display font-bold text-3xl lg:text-5xl tracking-tight max-w-2xl text-[#334155]">Have a specification in mind? We&apos;ll engineer it.</h2>
          <Link to="/contact" data-testid="products-cta" className="btn-fill group inline-flex items-center gap-3 border border-[#3da8d8] px-8 py-4 font-mono-accent text-xs uppercase tracking-widest text-[#3da8d8] hover:text-white">
            Talk to us <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
}
